import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminRevistas = ({
  API,
  getAuthHeaders,
  setCurrentView,
  loadRevistas,
  turmas,
  revistas,
  currentUser
}) => {
  const [loadingRevistas, setLoadingRevistas] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRevista, setEditingRevista] = useState(null);
  const [formData, setFormData] = useState({
    tema: '',
    turma_ids: [],
    licoes: [],
    valor: 0.0
  });

  useEffect(() => {
    // Carregar revistas apenas se não existirem
    if (revistas.length === 0) {
      loadRevistas();
    }
  }, []); // Array vazio - evita loop infinito

  // Inicializar lições vazias apenas quando necessário
  const initLicoes = () => {
    return Array.from({length: 13}, (_, i) => ({
      titulo: '',
      data: ''
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.tema || formData.turma_ids.length === 0) {
      alert('❌ Preencha o tema e selecione pelo menos uma turma');
      return;
    }

    setLoadingRevistas(true);

    try {
      const revistaData = {
        tema: formData.tema,
        turma_ids: formData.turma_ids,
        licoes: formData.licoes.filter(licao => licao.titulo && licao.data),
        valor: parseFloat(formData.valor) || 0.0
      };

      if (editingRevista) {
        await axios.put(`${API}/revistas/${editingRevista.id}`, revistaData, { headers: getAuthHeaders() });
        alert('✅ Revista atualizada!');
      } else {
        await axios.post(`${API}/revistas`, revistaData, { headers: getAuthHeaders() });
        alert('✅ Revista criada!');
      }

      await loadRevistas(true); // Force refresh para invalidar cache
      resetForm();
    } catch (error) {
      alert('❌ Erro ao salvar');
    } finally {
      setLoadingRevistas(false);
    }
  };

  const handleDelete = async (revistaId) => {
    if (window.confirm('Desativar esta revista?')) {
      try {
        setLoadingRevistas(true);
        await axios.delete(`${API}/revistas/${revistaId}`, { headers: getAuthHeaders() });
        alert('✅ Revista desativada!');
        await loadRevistas(true); // Force refresh para invalidar cache
      } catch (error) {
        alert('❌ Erro ao desativar');
      } finally {
        setLoadingRevistas(false);
      }
    }
  };

  const handleEdit = (revista) => {
    setEditingRevista(revista);
    setFormData({
      tema: revista.tema,
      turma_ids: revista.turma_ids,
      licoes: revista.licoes,
      valor: revista.valor || 0.0
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingRevista(null);
    setFormData({
      tema: '',
      turma_ids: [],
      licoes: []
    });
  };

  const handleShowForm = () => {
    if (!showForm) {
      setFormData({
        tema: '',
        turma_ids: [],
        licoes: Array.from({length: 13}, (_, i) => ({
          titulo: '',
          data: ''
        }))
      });
    }
    setShowForm(!showForm);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header Simples */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => setCurrentView('revistas')}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            ← Voltar
          </button>

          <button
            onClick={handleShowForm}
            disabled={loadingRevistas}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            {showForm ? '❌ Cancelar' : '➕ Nova Revista'}
          </button>
        </div>

        <h1 className="text-xl font-bold mb-6">⚙️ Administrar Revistas</h1>

        {/* Alerta de Demonstração */}
        {currentUser?.tipo === 'visitante' && (
          <div className="mb-6 p-4 bg-yellow-100 border border-yellow-400 rounded-lg">
            <div className="flex items-center">
              <span className="text-yellow-800 text-lg font-semibold">
                ⚠️ MODO DEMONSTRAÇÃO: Apenas Visualização
              </span>
            </div>
          </div>
        )}

        {/* Formulário Simples - só aparece quando necessário */}
        {showForm && (
          <div className="bg-white rounded-lg p-4 mb-6 border">
            <h3 className="font-bold mb-4">
              {editingRevista ? '✏️ Editar' : '➕ Nova Revista'}
            </h3>

            <form onSubmit={handleSave} className="space-y-4">
              {/* Tema */}
              <div>
                <label className="block text-sm font-medium mb-1">Tema *</label>
                <textarea
                  value={formData.tema}
                  onChange={(e) => setFormData({...formData, tema: e.target.value})}
                  required
                  rows={2}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Tema do trimestre..."
                />
              </div>

              {/* Valor da Revista */}
              <div>
                <label className="block text-sm font-medium mb-1">Valor da Revista (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor}
                  onChange={(e) => setFormData({...formData, valor: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Deixe 0.00 se a revista for gratuita</p>
              </div>

              {/* Turmas - Grid simples */}
              <div>
                <label className="block text-sm font-medium mb-2">Turmas *</label>
                <div className="grid grid-cols-2 gap-2">
                  {turmas.map(turma => (
                    <label key={turma.id} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={formData.turma_ids.includes(turma.id)}
                        onChange={(e) => {
                          const newIds = e.target.checked
                            ? [...formData.turma_ids, turma.id]
                            : formData.turma_ids.filter(id => id !== turma.id);
                          setFormData({...formData, turma_ids: newIds});
                        }}
                        className="mr-2"
                      />
                      {turma.nome}
                    </label>
                  ))}
                </div>
              </div>

              {/* Lições - TODAS AS 13 */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  📚 Lições do Trimestre (13 lições)
                </label>
                <div className="text-xs text-gray-600 mb-3">
                  Complete todas as lições com títulos e datas. Use formato: 2025-07-06
                </div>

                <div className="max-h-80 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                  {formData.licoes.map((licao, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 p-3 bg-white border rounded">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          📖 Lição {index + 1} - Título *
                        </label>
                        <input
                          type="text"
                          value={licao.titulo}
                          onChange={(e) => {
                            const newLicoes = [...formData.licoes];
                            newLicoes[index].titulo = e.target.value;
                            setFormData({...formData, licoes: newLicoes});
                          }}
                          placeholder={`Título da lição ${index + 1}`}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          📅 Data da Aula *
                        </label>
                        <input
                          type="date"
                          value={licao.data}
                          onChange={(e) => {
                            const newLicoes = [...formData.licoes];
                            newLicoes[index].data = e.target.value;
                            setFormData({...formData, licoes: newLicoes});
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-gray-600 mt-2 p-2 bg-blue-50 rounded">
                  💡 <strong>Dica:</strong> Preencha pelo menos as primeiras lições para começar. Você pode deixar algumas vazias e completar depois editando a revista.
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loadingRevistas}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {loadingRevistas ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de Revistas - Interface simplificada */}
        <div className="bg-white rounded-lg p-4">
          <h3 className="font-bold mb-4">📚 Revistas ({revistas.length})</h3>

          {loadingRevistas ? (
            <div className="text-center py-4">
              <div className="animate-spin text-lg mb-2">⟳</div>
              <p className="text-sm">Carregando...</p>
            </div>
          ) : revistas.length > 0 ? (
            <div className="space-y-3">
              {revistas.slice().reverse().map(revista => {
                const turmasNomes = turmas
                  .filter(t => revista.turma_ids.includes(t.id))
                  .map(t => t.nome)
                  .slice(0, 3)
                  .join(', ');

                return (
                  <div key={revista.id} className="border rounded p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 mr-4">
                        <h4 className="font-medium text-sm leading-tight mb-1">
                          {revista.tema.substring(0, 80)}
                          {revista.tema.length > 80 ? '...' : ''}
                        </h4>
                        <p className="text-xs text-gray-600">
                          {turmasNomes} • {revista.licoes.length} lições
                          {revista.valor > 0 && (
                            <span className="text-green-600 font-medium ml-2">
                              • R$ {revista.valor.toFixed(2).replace('.', ',')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(revista)}
                          className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(revista.id)}
                          className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhuma revista cadastrada</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRevistas;
